// Popup de célébration des badges.
//  • Mode normal : file d'attente, un médaillon fêté à la fois (badges gagnés en cours d'usage).
//  • Mode récap (summary) : 1er passage d'un utilisateur ayant déjà des badges → une seule
//    carte « Tu as déjà N badges ! » avec la rangée de médaillons (découverte de la feature).
// Entrée animée « sous-marine » : carte qui pop, halo bleu + rayons dorés (god rays) + anneau
// d'onde, médaillon qui rebondit puis flotte, flux de bulles, étincelles, et un « bloup » synthétisé.
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BADGES } from "../badges";
import BadgeMedal from "./BadgeMedal";
import { playBubble } from "../utils/sound";

const BY_KEY = Object.fromEntries(BADGES.map((b) => [b.key, b]));

// Bulles décoratives : positions/tailles/tempos variés (déterministes).
const BUBBLES = [
    { left: 12, size: 7, delay: 0.0, dur: 2.0, drift: 6 },
    { left: 22, size: 12, delay: 0.5, dur: 2.6, drift: -8 },
    { left: 33, size: 6, delay: 1.1, dur: 2.2, drift: 4 },
    { left: 44, size: 10, delay: 0.3, dur: 2.9, drift: -5 },
    { left: 52, size: 8, delay: 0.9, dur: 2.3, drift: 7 },
    { left: 61, size: 14, delay: 0.15, dur: 3.1, drift: -6 },
    { left: 70, size: 7, delay: 0.7, dur: 2.5, drift: 5 },
    { left: 79, size: 11, delay: 1.3, dur: 2.8, drift: -7 },
    { left: 88, size: 6, delay: 0.45, dur: 2.1, drift: 4 },
    { left: 17, size: 9, delay: 1.6, dur: 2.7, drift: -4 },
    { left: 65, size: 8, delay: 1.9, dur: 2.4, drift: 6 },
];
const SPARKLES = [
    { top: 14, left: 30, delay: 0.6 },
    { top: 22, left: 70, delay: 1.1 },
    { top: 8, left: 58, delay: 1.5 },
];

// Fond animé commun aux deux modes (rayons dorés + halo bleu + anneau + bulles + étincelles).
function Scene({ ping, children }) {
    return (
        <div style={{ position: "relative", minHeight: 132, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {/* Rayons dorés (god rays) qui tournent lentement */}
            <div className="sda-cel-anim sda-cel-hide" aria-hidden="true" style={{
                position: "absolute", width: 190, height: 190, borderRadius: "50%",
                background: "repeating-conic-gradient(from 0deg, rgba(255,196,84,0.20) 0deg 6deg, transparent 6deg 22deg)",
                WebkitMaskImage: "radial-gradient(circle, #000 18%, transparent 62%)",
                maskImage: "radial-gradient(circle, #000 18%, transparent 62%)",
                animation: "sdaSpin 14s linear infinite",
            }} />
            {/* Halo lumineux pulsé (bleu) */}
            <div className="sda-cel-anim" aria-hidden="true" style={{
                position: "absolute", width: 150, height: 150, borderRadius: "50%",
                background: "radial-gradient(circle, rgba(0,150,199,0.34) 0%, rgba(0,150,199,0.10) 45%, transparent 70%)",
                animation: "sdaHalo 3.2s ease-in-out .1s infinite both",
            }} />
            {/* Anneau d'onde (splash) qui part une fois */}
            {ping && (
                <div className="sda-cel-anim sda-cel-hide" aria-hidden="true" style={{
                    position: "absolute", width: 96, height: 96, borderRadius: "50%",
                    border: "2px solid rgba(0,150,199,0.55)", animation: "sdaPing .9s ease-out .15s 1 both",
                }} />
            )}
            {/* Bulles */}
            {BUBBLES.map((bb, k) => (
                <span key={k} className="sda-cel-hide" aria-hidden="true" style={{
                    position: "absolute", bottom: 6, left: `${bb.left}%`,
                    width: bb.size, height: bb.size, borderRadius: "50%",
                    background: "rgba(0,150,199,0.28)", border: "1px solid rgba(0,150,199,0.45)",
                    animation: `sdaRise ${bb.dur}s ${bb.delay}s ease-in infinite`,
                    "--drift": `${bb.drift}px`,
                }} />
            ))}
            {/* Étincelles dorées */}
            {SPARKLES.map((sp, k) => (
                <span key={`s${k}`} className="sda-cel-hide" aria-hidden="true" style={{
                    position: "absolute", top: `${sp.top}%`, left: `${sp.left}%`,
                    width: 4, height: 4, borderRadius: "50%", background: "#fff",
                    boxShadow: "0 0 6px 2px rgba(255,205,120,0.9)",
                    animation: `sdaTwinkle 2.4s ${sp.delay}s ease-in-out infinite`,
                }} />
            ))}
            <div style={{ position: "relative", zIndex: 2 }}>{children}</div>
        </div>
    );
}

export default function BadgeCelebration({ queue, summary = false, onDone }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const keys = (queue || []).filter((k) => BY_KEY[k]);
    const [i, setI] = useState(0);

    // Repart à zéro quand une nouvelle file arrive.
    useEffect(() => { setI(0); }, [queue]);
    // Joue le son à chaque badge affiché (une fois en mode récap).
    useEffect(() => { if (keys.length && i < keys.length) playBubble(); }, [i, keys.length]);

    if (!keys.length) return null;
    const close = () => onDone && onDone();
    const goToBadges = () => { close(); navigate("/profile"); };

    const cardStyle = { background: "linear-gradient(180deg, #ffffff 0%, #f2fafe 100%)", borderRadius: 22, padding: "30px 26px 22px", maxWidth: 340, width: "100%", textAlign: "center", boxShadow: "0 24px 70px rgba(2,28,45,0.45), 0 0 0 1px rgba(0,150,199,0.08)", position: "relative", overflow: "hidden", animation: "sdaPop .55s cubic-bezier(.16,1.15,.3,1) both" };
    const buttonStyle = { marginTop: 16, background: "var(--primary-blue)", color: "#fff", border: "none", borderRadius: 12, padding: "11px 26px", fontWeight: 700, fontSize: 15, cursor: "pointer", boxShadow: "0 6px 18px rgba(0,119,182,0.35)" };
    const eyebrow = { fontFamily: "var(--font-display)", fontWeight: 800, color: "var(--primary-blue)", fontSize: 12.5, letterSpacing: ".08em", textTransform: "uppercase" };

    // ── Mode récap : « Tu as déjà N badges ! » ──────────────────────────────
    if (summary) {
        return (
            <div className="sda-cel" onClick={close} style={overlayStyle}>
                <div key="summary" onClick={(e) => e.stopPropagation()} style={cardStyle}>
                    <Scene>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", maxWidth: 260 }}>
                            {keys.map((k, idx) => (
                                <div key={k} className="sda-cel-anim" style={{ animation: `sdaMedalIn .5s cubic-bezier(.2,1.4,.35,1) ${(0.05 + idx * 0.05).toFixed(2)}s both` }}>
                                    <BadgeMedal badge={BY_KEY[k]} earned size={36} />
                                </div>
                            ))}
                        </div>
                    </Scene>
                    <div className="sda-cel-anim" style={{ ...eyebrow, marginTop: 6, animation: "sdaTextIn .5s ease-out .25s both" }}>{t("badges.title")}</div>
                    <div className="sda-cel-anim" style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.3rem", color: "#223344", marginTop: 4, animation: "sdaTextIn .5s ease-out .33s both" }}>{t("badges.alreadyTitle", { count: keys.length })}</div>
                    <div className="sda-cel-anim" style={{ color: "#556", fontSize: 14, marginTop: 5, lineHeight: 1.4, animation: "sdaTextIn .5s ease-out .4s both" }}>{t("badges.alreadySubtitle")}</div>
                    <button onClick={goToBadges} style={buttonStyle}>{t("badges.viewBadges")}</button>
                </div>
                <StyleBlock />
            </div>
        );
    }

    // ── Mode normal : un badge à la fois ────────────────────────────────────
    if (i >= keys.length) return null;
    const b = BY_KEY[keys[i]];
    const isLast = i + 1 >= keys.length;
    const advance = () => { if (!isLast) setI(i + 1); else close(); };

    return (
        <div className="sda-cel" onClick={close} style={overlayStyle}>
            <div key={keys[i]} onClick={(e) => e.stopPropagation()} style={cardStyle}>
                <Scene ping>
                    <div className="sda-cel-anim" style={{ animation: "sdaMedalIn .6s cubic-bezier(.2,1.4,.35,1) .05s both" }}>
                        <div className="sda-cel-anim" style={{ animation: "sdaFloat 3.4s ease-in-out .7s infinite both" }}>
                            <BadgeMedal badge={b} earned size={84} />
                        </div>
                    </div>
                </Scene>
                <div className="sda-cel-anim" style={{ ...eyebrow, marginTop: 4, animation: "sdaTextIn .5s ease-out .25s both" }}>{t("badges.unlocked")}</div>
                <div className="sda-cel-anim" style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.3rem", color: "#223344", marginTop: 4, animation: "sdaTextIn .5s ease-out .33s both" }}>{t(`badges.items.${b.key}.name`)}</div>
                <div className="sda-cel-anim" style={{ color: "#556", fontSize: 14, marginTop: 5, lineHeight: 1.4, animation: "sdaTextIn .5s ease-out .4s both" }}>{t(`badges.items.${b.key}.desc`)}</div>
                {keys.length > 1 && <div style={{ color: "#9aa7b0", fontSize: 12, marginTop: 10, fontVariantNumeric: "tabular-nums" }}>{i + 1} / {keys.length}</div>}
                <button onClick={isLast ? goToBadges : advance} style={buttonStyle}>{isLast ? t("badges.viewBadges") : t("badges.next")}</button>
            </div>
            <StyleBlock />
        </div>
    );
}

const overlayStyle = { position: "fixed", inset: 0, zIndex: 10050, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, background: "radial-gradient(120% 120% at 50% 40%, rgba(2,28,45,0.42), rgba(2,18,30,0.66))", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)", animation: "sdaFade .3s ease-out both" };

function StyleBlock() {
    return (
        <style>{`
            @keyframes sdaFade{from{opacity:0}to{opacity:1}}
            @keyframes sdaPop{0%{opacity:0;transform:translateY(16px) scale(.86)}60%{opacity:1;transform:translateY(-3px) scale(1.02)}100%{opacity:1;transform:none}}
            @keyframes sdaMedalIn{0%{opacity:0;transform:scale(0) rotate(-22deg)}60%{opacity:1;transform:scale(1.16) rotate(6deg)}100%{opacity:1;transform:scale(1) rotate(0)}}
            @keyframes sdaFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
            @keyframes sdaHalo{0%,100%{opacity:.45;transform:scale(1)}50%{opacity:.8;transform:scale(1.1)}}
            @keyframes sdaSpin{from{transform:rotate(0)}to{transform:rotate(360deg)}}
            @keyframes sdaPing{0%{opacity:.75;transform:scale(.35)}100%{opacity:0;transform:scale(1.9)}}
            @keyframes sdaRise{0%{transform:translateY(22px) translateX(0) scale(.4);opacity:0}20%{opacity:.85}100%{transform:translateY(-96px) translateX(var(--drift,0)) scale(1);opacity:0}}
            @keyframes sdaTwinkle{0%,100%{opacity:0;transform:scale(.4)}50%{opacity:1;transform:scale(1)}}
            @keyframes sdaTextIn{0%{opacity:0;transform:translateY(7px)}100%{opacity:1;transform:none}}
            @media (prefers-reduced-motion: reduce){
                .sda-cel .sda-cel-anim{animation:none!important}
                .sda-cel .sda-cel-hide{display:none!important}
            }
        `}</style>
    );
}
